import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';

import { getMemberSessionAndReportSummaries } from '../../../../api/report/report';
import { MemberSessionAndReportSummaries, MemberSessionAndReportSummary } from '../../../../api/report/types';
import { AppDispatch, RootState } from '../../../../app/store';
import { stateNeedsUpdating } from '../../../../reduxHelpers/stateHelpers';
import { emptyReturnObject, RequestStatusWithDetails } from '../../../../types/RequestStatusWithDetails';
import { MemberDataSlice } from '../slice';

export const asyncLoadMemberSessionAndReportSummaries = createAsyncThunk<
    MemberSessionAndReportSummaries,
    { memberId: string; force?: boolean },
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>(
    'member/sessionsAndReportSummaries',
    async (args) => {
        return await getMemberSessionAndReportSummaries(args.memberId);
    },
    {
        condition: (args, { getState }) => {
            const { members } = getState();

            const sessionAndReportSummaries = members.memberSessionAndReportSummaries[args.memberId];

            return !!args.force || stateNeedsUpdating(sessionAndReportSummaries);
        },
    }
);

const reducer = (builder: ActionReducerMapBuilder<MemberDataSlice>) => {
    builder.addCase(asyncLoadMemberSessionAndReportSummaries.pending, (state, action) => {
        const existingMemberState =
            state.memberSessionAndReportSummaries[action.meta.arg.memberId] ?? emptyReturnObject();

        state.memberSessionAndReportSummaries[action.meta.arg.memberId] = {
            ...existingMemberState,
            status: existingMemberState.loadedTimestamp ? 'reloading' : 'pending',
        } as RequestStatusWithDetails<MemberSessionAndReportSummary[]>;
    });

    builder.addCase(asyncLoadMemberSessionAndReportSummaries.rejected, (state, action) => {
        state.memberSessionAndReportSummaries[action.meta.arg.memberId].status = 'error';
    });

    builder.addCase(asyncLoadMemberSessionAndReportSummaries.fulfilled, (state, action) => {
        state.memberSessionAndReportSummaries[action.meta.arg.memberId].status = 'success';
        state.memberSessionAndReportSummaries[action.meta.arg.memberId].loadedTimestamp = Date.now();
        state.memberSessionAndReportSummaries[action.meta.arg.memberId].data = orderBy(
            action.payload.sessionAndReportSummaries,
            (summary) => summary.date,
            'desc'
        );
    });
};

export default reducer;
